/**
 * 10.8F — TELA DE RANKING + NOME. Mostra o nome do jogador (editável) e o placar público
 * em 4 abas: Profundidade+Nível · Poder · PvP · Abates. Lê/escreve via CloudProfile (Supabase).
 * Autocontida (só recebe um callback pra pegar as stats atuais do jogador).
 */
import { fetchRanking, getName, sanitizeName, setNameLocal, syncProfile, type ProfileStats, type RankField, type RankRow } from "../net/CloudProfile";
import { GameConfig } from "../config/GameConfig";
import { authState, createAccount, login, logout, signInWithProvider } from "../net/CloudAuth";
import { fmtNum } from "../utils/format";
import { fetchSeasonRanking, fmtLeft, seasonInfo, SEASON_CATEGORIES, SEASON_PRIZES, type SeasonField } from "../net/CloudSeason";

interface TabDef {
  field: RankField;
  label: string;
  value: (r: RankRow) => string;
}
const TABS: TabDef[] = [
  { field: "depth", label: "Profundidade", value: (r) => `Prof. ${r.depth} · Nv ${r.level}` },
  { field: "power", label: "Poder", value: (r) => fmtNum(r.power) },
  { field: "pvp_wins", label: "PvP", value: (r) => `${r.pvp_wins} vit.` },
  { field: "kills", label: "Abates", value: (r) => fmtNum(r.kills) },
];

export class RankingScreen {
  private readonly root: HTMLDivElement;
  private readonly nameInput: HTMLInputElement;
  private readonly tabsEl: HTMLElement;
  private readonly listEl: HTMLElement;
  private active: RankField | "season" = "depth";
  private loadToken = 0;
  private seasonEl!: HTMLElement;
  private seasonField: SeasonField = "season_kills";

  constructor(
    uiRoot: HTMLElement,
    private readonly onClose: () => void,
    private readonly getStats: () => ProfileStats,
  ) {
    injectStyles();
    this.root = document.createElement("div");
    this.root.className = "overlay rank-overlay";
    this.root.hidden = true;
    this.root.innerHTML = `
      <div class="panel void-panel rank-panel">
        <div class="rank-head">
          <h2 class="rank-title">🏆 Ranking</h2>
          <button class="btn rank-x" data-a="close">✕</button>
        </div>
        <div class="rank-name">
          <label class="rank-name-lbl" for="vh-ranking-name">Seu nome</label>
          <div class="rank-name-row">
            <input id="vh-ranking-name" class="rank-name-input" type="text" maxlength="16" placeholder="Digite seu nome" />
            <button class="btn primary rank-name-save" data-a="save">Salvar</button>
          </div>
        </div>
        <div class="rank-acct">
          <div class="rank-acct-top">
            <span class="rank-acct-status"></span>
            <button class="rank-acct-toggle" data-a="acct-toggle"></button>
          </div>
          <div class="rank-acct-hint"></div>
          <div class="rank-acct-form" hidden>
            <!-- 10.9IC — login social: 1 toque, sem digitar nada. O e-mail+senha abaixo é o "ID do Void". -->
            <div class="rank-acct-social"${GameConfig.auth.social ? "" : " hidden"}>
              <button class="btn acct-social g" data-a="acct-google"${GameConfig.auth.google ? "" : " hidden"}><span>G</span> Entrar com Google</button>
              <button class="btn acct-social f" data-a="acct-facebook"${GameConfig.auth.facebook ? "" : " hidden"}><span>f</span> Entrar com Facebook</button>
            </div>
            <div class="rank-acct-or"${GameConfig.auth.social ? "" : " hidden"}>ou use um ID do Void</div>
            <input class="rank-acct-email" type="email" autocomplete="email" aria-label="E-mail" placeholder="E-mail" />
            <input class="rank-acct-pass" type="password" autocomplete="current-password" aria-label="Senha (6+ caracteres)" placeholder="Senha (6+ caracteres)" />
            <div class="rank-acct-btns">
              <button class="btn primary" data-a="acct-signup">Criar conta</button>
              <button class="btn" data-a="acct-login">Entrar</button>
            </div>
            <div class="rank-acct-msg"></div>
          </div>
        </div>
        <div class="rank-tabs"></div>
        <div class="rank-season" hidden>
          <div class="rank-season-head"><b class="rank-season-no"></b><span class="rank-season-left"></span></div>
          <div class="rank-season-cats"></div>
          <div class="rank-season-prizes"></div>
        </div>
        <div class="rank-list"></div>
      </div>`;
    uiRoot.appendChild(this.root);
    this.nameInput = this.root.querySelector(".rank-name-input")!;
    this.tabsEl = this.root.querySelector(".rank-tabs")!;
    this.listEl = this.root.querySelector(".rank-list")!;

    this.root.querySelector('[data-a="close"]')!.addEventListener("click", () => this.onClose());
    this.root.addEventListener("click", (e) => {
      if (e.target === this.root) this.onClose();
    });
    this.root.querySelector('[data-a="save"]')!.addEventListener("click", () => this.saveName());
    this.nameInput.addEventListener("keydown", (e) => {
      if ((e as KeyboardEvent).key === "Enter") this.saveName();
    });

    // 10.9BA — aba TEMPORADA (7 dias, top 10 ganha 💎 por Abates e PvP feitos DENTRO dela)
    this.tabsEl.innerHTML =
      `<button class="rank-tab season" data-f="season">🏅 Temporada</button>` +
      TABS.map((t, i) => `<button class="rank-tab${i === 0 ? " active" : ""}" data-f="${t.field}">${t.label}</button>`).join("");
    this.tabsEl.querySelectorAll<HTMLButtonElement>(".rank-tab").forEach((b) => {
      b.onclick = () => {
        this.active = b.dataset.f as RankField | "season";
        this.tabsEl.querySelectorAll(".rank-tab").forEach((x) => x.classList.toggle("active", x === b));
        this.seasonEl.hidden = this.active !== "season";
        void this.load();
      };
    });
    this.seasonEl = this.root.querySelector(".rank-season")!;
    const cats = this.root.querySelector<HTMLElement>(".rank-season-cats")!;
    cats.innerHTML = SEASON_CATEGORIES.map((c, i) => `<button class="rank-cat${i === 0 ? " active" : ""}" data-c="${c.field}">${c.label}</button>`).join("");
    cats.querySelectorAll<HTMLButtonElement>(".rank-cat").forEach((b) => {
      b.onclick = () => {
        this.seasonField = b.dataset.c as SeasonField;
        cats.querySelectorAll(".rank-cat").forEach((x) => x.classList.toggle("active", x === b));
        void this.load();
      };
    });
    this.root.querySelector<HTMLElement>(".rank-season-prizes")!.textContent =
      `Top 10 ganha 💎 no correio ao fechar: ${SEASON_PRIZES.slice(0, 3).map((d, i) => `${i + 1}º ${d}`).join(" · ")} · 4º–10º ${SEASON_PRIZES[3]}–${SEASON_PRIZES[9]}`;

    // 10.8H — conta por e-mail
    this.root.querySelector('[data-a="acct-toggle"]')!.addEventListener("click", () => this.onAcctToggle());
    this.root.querySelector('[data-a="acct-google"]')!.addEventListener("click", () => void this.doSocial("google")); // 10.9IC
    this.root.querySelector('[data-a="acct-facebook"]')!.addEventListener("click", () => void this.doSocial("facebook")); // 10.9IC
    this.root.querySelector('[data-a="acct-signup"]')!.addEventListener("click", () => this.doAuth("signup"));
    this.root.querySelector('[data-a="acct-login"]')!.addEventListener("click", () => this.doAuth("login"));
  }

  open(): void {
    this.nameInput.value = getName();
    this.root.hidden = false;
    void this.refreshAccount();
    // sobe o meu perfil (nome + stats atuais) e então carrega o placar
    void syncProfile(this.getStats()).then(() => this.load());
    void this.load(); // mostra algo já enquanto o sync termina
  }

  hide(): void {
    this.root.hidden = true;
  }

  private saveName(): void {
    const clean = sanitizeName(this.nameInput.value);
    if (!clean) return;
    setNameLocal(clean);
    this.nameInput.value = clean;
    void syncProfile(this.getStats()).then(() => this.load());
  }

  // ---- 10.8H — conta por e-mail ----
  private async refreshAccount(): Promise<void> {
    const status = this.root.querySelector<HTMLElement>(".rank-acct-status")!;
    const toggle = this.root.querySelector<HTMLButtonElement>('[data-a="acct-toggle"]')!;
    const form = this.root.querySelector<HTMLElement>(".rank-acct-form")!;
    const hint = this.root.querySelector<HTMLElement>(".rank-acct-hint")!;
    const st = await authState();
    if (st.signedIn && !st.anonymous) {
      // 10.9IC — mostra COMO entrou (Google/Facebook/ID do Void) e o nome, quando o provedor manda
      const via = st.provider === "google" ? "Google" : st.provider === "facebook" ? "Facebook" : "ID do Void";
      const quem = st.name || st.email || via;
      status.textContent = `✅ ${quem} · ${via}`;
      hint.textContent = "Seu progresso está salvo nesta conta. Entre com ela em outro aparelho pra continuar de onde parou.";
      toggle.textContent = "Sair";
      toggle.dataset.mode = "logout";
      form.hidden = true;
    } else {
      status.textContent = "🔓 Jogando como convidado";
      // 10.9FJ — Thiago: login por e-mail+senha (seguro e guardável). Form ABERTO pro convidado ver.
      hint.textContent = "Crie uma conta com e-mail e senha pra NÃO perder seu progresso e poder jogar em outro aparelho.";
      toggle.textContent = "";
      toggle.hidden = true;
      form.hidden = false;
    }
  }

  private onAcctToggle(): void {
    const toggle = this.root.querySelector<HTMLButtonElement>('[data-a="acct-toggle"]')!;
    if (toggle.dataset.mode === "logout") {
      void logout().then(() => location.reload());
      return;
    }
    const form = this.root.querySelector<HTMLElement>(".rank-acct-form")!;
    form.hidden = !form.hidden;
  }

  /** 10.9IC — login social (Google/Facebook). Sai da página pro provedor e volta logado. */
  private async doSocial(provider: "google" | "facebook"): Promise<void> {
    const msg = this.root.querySelector<HTMLElement>(".rank-acct-msg")!;
    msg.textContent = "…";
    msg.style.color = "";
    const nm = sanitizeName(this.nameInput.value);
    if (nm) setNameLocal(nm); // guarda o nome digitado antes de sair pro provedor
    // 10.9IL — no APK o resultado chega pelo evento, depois que o jogador volta da aba do Chrome.
    window.addEventListener("vh-auth", (e) => {
      const d = (e as CustomEvent<{ ok: boolean; msg: string }>).detail;
      msg.textContent = d.msg;
      msg.style.color = d.ok ? "#8affc0" : "#ff9a9a";
    }, { once: true });
    const r = await signInWithProvider(provider);
    msg.textContent = r.msg;
    msg.style.color = r.ok ? "#8affc0" : "#ff9a9a";
  }

  private async doAuth(kind: "signup" | "login"): Promise<void> {
    const email = this.root.querySelector<HTMLInputElement>(".rank-acct-email")!.value;
    const pass = this.root.querySelector<HTMLInputElement>(".rank-acct-pass")!.value;
    const msg = this.root.querySelector<HTMLElement>(".rank-acct-msg")!;
    msg.textContent = "…";
    // salva o nome digitado junto (se houver) antes de criar/entrar
    const nm = sanitizeName(this.nameInput.value);
    if (nm) setNameLocal(nm);
    const r = kind === "signup" ? await createAccount(email, pass) : await login(email, pass);
    msg.textContent = r.msg;
    msg.style.color = r.ok ? "#8affc0" : "#ff9a9a";
    if (r.ok && kind === "login") {
      // trocou de conta → recarrega pra puxar o save/perfil dela
      setTimeout(() => location.reload(), 700);
    } else if (r.ok && kind === "signup" && !r.needConfirm) {
      await syncProfile(this.getStats()); // já vincula o perfil à conta
      await this.refreshAccount();
    }
  }

  private async load(): Promise<void> {
    const token = ++this.loadToken;
    this.listEl.innerHTML = `<div class="rank-empty">Carregando…</div>`;
    if (this.active === "season") return this.loadSeason(token);
    const tab = TABS.find((t) => t.field === this.active)!;
    const { rows, myId } = await fetchRanking(this.active, 50);
    if (token !== this.loadToken) return; // troquei de aba antes de terminar
    if (rows.length === 0) {
      this.listEl.innerHTML = `<div class="rank-empty">Ninguém no ranking ainda.<br>Jogue e volte aqui!</div>`;
      return;
    }
    this.listEl.innerHTML = rows.map((r, i) => this.row(i + 1, r.user_id === myId, r.name, tab.value(r), r.class_id)).join("");
  }

  private row(pos: number, isMe: boolean, name: string, val: string, classId?: string | null): string {
    const icon = classId ? GameConfig.classes.find((c) => c.id === classId)?.icon : undefined; // 10.9L28 — ícone da classe do personagem
    const label = icon ? `${icon} ${name || "Caçador"}` : name || "Caçador";
    const me = isMe ? " me" : "";
    const medal = pos === 1 ? "🥇" : pos === 2 ? "🥈" : pos === 3 ? "🥉" : `${pos}`;
    return `<div class="rank-row${me}"><span class="rank-pos">${medal}</span><span class="rank-name-cell">${escapeHtml(label)}${me ? " (você)" : ""}</span><b class="rank-val">${val}</b></div>`;
  }

  /** 10.9BA — placar da temporada atual (só o que foi feito DENTRO dela). */
  private async loadSeason(token: number): Promise<void> {
    const s = seasonInfo();
    this.root.querySelector<HTMLElement>(".rank-season-no")!.textContent = `Temporada ${s.no}`;
    this.root.querySelector<HTMLElement>(".rank-season-left")!.textContent = `termina em ${fmtLeft(s.msLeft)}`;
    const { rows, myId } = await fetchSeasonRanking(this.seasonField, s.no, 50);
    if (token !== this.loadToken) return;
    if (rows.length === 0) {
      this.listEl.innerHTML = `<div class="rank-empty">Ninguém pontuou nesta temporada ainda.<br>Cace e volte aqui!</div>`;
      return;
    }
    const unit = this.seasonField === "season_pvp" ? "vit." : "abates";
    this.listEl.innerHTML = rows
      .map((r, i) => this.row(i + 1, r.user_id === myId, r.name, `${fmtNum(r[this.seasonField])} ${unit}${i < SEASON_PRIZES.length ? ` · 💎${SEASON_PRIZES[i]}` : ""}`))
      .join("");
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c] ?? c);
}

let stylesInjected = false;
function injectStyles(): void {
  if (stylesInjected || typeof document === "undefined") return;
  stylesInjected = true;
  const css = `
  .rank-panel{max-width:440px;width:92%;max-height:86vh;display:flex;flex-direction:column;gap:12px}
  .rank-head{display:flex;align-items:center;justify-content:space-between}
  .rank-title{margin:0;font-size:20px;color:#eaf1ff}
  .rank-x{padding:6px 12px}
  .rank-name{background:rgba(124,77,255,.1);border:1px solid rgba(124,77,255,.35);border-radius:12px;padding:10px 12px}
  .rank-name-lbl{display:block;font-size:11px;letter-spacing:.14em;text-transform:uppercase;color:#9fb4ff;margin-bottom:6px}
  .rank-name-row{display:flex;gap:8px}
  .rank-name-input{flex:1;min-width:0;background:rgba(8,10,22,.85);border:1px solid rgba(157,123,255,.5);border-radius:10px;color:#eaf1ff;font:600 15px system-ui,sans-serif;padding:9px 12px}
  .rank-name-input:focus{outline:none;border-color:#2be0ff}
  .rank-name-save{flex-shrink:0}
  .rank-tabs{display:flex;gap:6px;flex-wrap:wrap}
  .rank-tab{flex:1;min-width:72px;background:rgba(8,10,22,.6);border:1px solid rgba(124,77,255,.3);border-radius:10px;color:#b9c4e6;font:600 12px system-ui,sans-serif;padding:8px 4px;cursor:pointer}
  .rank-tab.active{background:linear-gradient(180deg,#7c4dff,#5a34d6);border-color:#9d7bff;color:#fff}
  .rank-tab.season{flex-basis:100%;background:linear-gradient(180deg,rgba(255,213,74,.16),rgba(255,213,74,.06));border-color:rgba(255,213,74,.45);color:#ffd54a}
  .rank-tab.season.active{background:linear-gradient(180deg,#d9a520,#a87a12);border-color:#ffd54a;color:#1a1200}
  .rank-season{background:rgba(255,213,74,.07);border:1px solid rgba(255,213,74,.3);border-radius:12px;padding:9px 12px;display:flex;flex-direction:column;gap:7px}
  .rank-season-head{display:flex;justify-content:space-between;align-items:baseline;gap:8px}
  .rank-season-no{color:#ffd54a;font-size:15px}
  .rank-season-left{color:#c9d4f0;font-size:12px}
  .rank-season-cats{display:flex;gap:6px}
  .rank-cat{flex:1;background:rgba(8,10,22,.6);border:1px solid rgba(255,213,74,.3);border-radius:9px;color:#e6d9a8;font:600 12px system-ui,sans-serif;padding:7px 4px;cursor:pointer}
  .rank-cat.active{background:#ffd54a;color:#1a1200;border-color:#ffd54a}
  .rank-season-prizes{font-size:11px;line-height:1.45;color:#b9c4e6}
  .rank-list{flex:1;overflow-y:auto;-webkit-overflow-scrolling:touch;display:flex;flex-direction:column;gap:5px;min-height:120px}
  .rank-row{display:flex;align-items:center;gap:10px;background:rgba(8,10,22,.55);border:1px solid rgba(124,77,255,.16);border-radius:10px;padding:9px 12px}
  .rank-row.me{background:rgba(43,224,255,.14);border-color:rgba(43,224,255,.55)}
  .rank-pos{width:30px;text-align:center;font-weight:800;color:#ffd54a;font-size:15px;flex-shrink:0}
  .rank-name-cell{flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:#eaf1ff;font-weight:600}
  .rank-val{color:#9fe6ff;font-weight:800;flex-shrink:0}
  .rank-empty{text-align:center;color:#9aa7d6;padding:28px 12px;line-height:1.6}
  .rank-acct{background:rgba(43,224,255,.07);border:1px solid rgba(43,224,255,.25);border-radius:12px;padding:9px 12px}
  .rank-acct-top{display:flex;align-items:center;justify-content:space-between;gap:8px}
  .rank-acct-status{font-size:13px;color:#c9d4f0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
  .rank-acct-toggle{flex-shrink:0;background:transparent;border:1px solid rgba(43,224,255,.5);border-radius:8px;color:#9fe6ff;font:600 12px system-ui,sans-serif;padding:6px 10px;cursor:pointer}
  .rank-acct-form{display:flex;flex-direction:column;gap:7px;margin-top:9px}
  .rank-acct-form input{background:rgba(8,10,22,.85);border:1px solid rgba(157,123,255,.5);border-radius:9px;color:#eaf1ff;font:600 14px system-ui,sans-serif;padding:9px 11px}
  .rank-acct-form input:focus{outline:none;border-color:#2be0ff}
  .rank-acct-btns{display:flex;gap:8px}
  .rank-acct-btns .btn{flex:1}
  .rank-acct-msg{font-size:12px;line-height:1.4;min-height:1em;color:#9aa7d6}`;
  const el = document.createElement("style");
  el.textContent = css;
  document.head.appendChild(el);
}
